/**
 * A persona definition: an agent "employee" described as data rather than code. A tenant stores one per hire or per
 * app that ships it (tenant_personas), and the API builds a subagent from it at run time: the instructions become its
 * system prompt, the capabilities pick its tools from the catalogue, the scope says which object types those tools
 * may read and write, and the model tier and effort set the model. Everything here is checked against the catalogue
 * and the tenant's merged registry before it is stored, so a definition that activates is one the runtime can build.
 */
import { z } from 'zod';
import { AGENT_SLUGS } from '../agents';
import { PERSONA_CAPABILITIES, PERSONA_CAPABILITY_KEYS, PROJECTS_APP_TYPES, type PersonaCapability, type PersonaCapabilityDefinition } from './catalogue';
import { isTimeZone, parseCron } from './cron';

/** A persona slug: snake_case like the compiled agents', 2 to 40 characters. */
export const PERSONA_SLUG_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;
/** Every type in the tenant, as a scope entry. */
export const SCOPE_ALL = '*';
/** An object type key a scope may name: a built-in type, an app type, or `*`. */
const SCOPE_TYPE_PATTERN = /^(\*|app:[a-z][a-z0-9-]{1,39}:[A-Z][A-Za-z0-9]{0,63}|[A-Z][A-Za-z0-9]{0,63})$/;

export const PERSONA_MODELS = ['opus', 'sonnet'] as const;
export type PersonaModel = (typeof PERSONA_MODELS)[number];
export const PERSONA_EFFORTS = ['low', 'medium', 'high'] as const;
export type PersonaEffort = (typeof PERSONA_EFFORTS)[number];

/** As long as a plan step's task may be, less the lines the approval summary adds around the instructions. */
export const PERSONA_INSTRUCTIONS_MAX = 2500;

export const PersonaScheduleSchema = z
  .object({
    /** Five-field cron, read in `timeZone`. */
    cron: z.string().min(1).max(60),
    /** What the persona does each time it fires: the standing brief its Chief of Staff hands it. */
    brief: z.string().min(1).max(2000),
    /** An IANA time zone; UTC when left out. */
    timeZone: z.string().min(1).max(64).optional(),
  })
  .strict();

export const PersonaScopeSchema = z
  .object({
    /** The object types it may read; `*` for every type the tenant has. Empty only for a persona whose capabilities touch no records. */
    read: z.array(z.string().regex(SCOPE_TYPE_PATTERN, 'a scope names an object type such as Task or app:projects:Project, or *')).max(40),
    /** The object types it may create, update, link and delete, each also readable. Empty for a persona that only reads. */
    write: z.array(z.string().regex(SCOPE_TYPE_PATTERN, 'a scope names an object type such as Task or app:projects:Project, or *')).max(40).default([]),
  })
  .strict();

export const PersonaDefinitionSchema = z
  .object({
    slug: z.string().regex(PERSONA_SLUG_PATTERN, 'a slug is snake_case, such as release_coordinator'),
    displayName: z.string().min(1).max(60),
    /** What it does, in a sentence or two, for the roster and for Chief of Staff when it plans. */
    description: z.string().min(1).max(500),
    /** Its system prompt, in markdown: who it is, how it works, what good looks like. */
    instructions: z.string().min(1).max(PERSONA_INSTRUCTIONS_MAX),
    model: z.enum(PERSONA_MODELS).default('sonnet'),
    effort: z.enum(PERSONA_EFFORTS).default('low'),
    capabilities: z.array(z.enum(PERSONA_CAPABILITY_KEYS)).min(1).max(PERSONA_CAPABILITY_KEYS.length),
    scope: PersonaScopeSchema,
    schedule: PersonaScheduleSchema.optional(),
  })
  .strict();

export type PersonaDefinition = z.infer<typeof PersonaDefinitionSchema>;
/** A definition as an agent or a manifest writes it, before defaults are applied. */
export type PersonaDefinitionInput = z.input<typeof PersonaDefinitionSchema>;
export type PersonaSchedule = z.infer<typeof PersonaScheduleSchema>;

/** The tenant's object types a definition is checked against: the keys of its merged registry. */
export interface PersonaValidationContext {
  nodeTypes: readonly string[];
  /** Slugs a persona may not take, beyond the compiled agents'. */
  reservedSlugs?: readonly string[];
  /** True when a super admin linked the tenant to a Lantern workspace, which a staff capability needs; false when left out. */
  lanternLinked?: boolean;
}

/** The capabilities that work on the tenant's records, and so need read_records and a read scope. */
const RECORD_CAPABILITIES: readonly PersonaCapability[] = ['read_records', 'write_records', 'lists', 'picklists', 'dashboards', 'review_projects'];

function isStaffCapability(capability: string): boolean {
  return (PERSONA_CAPABILITY_KEYS as readonly string[]).includes(capability) && (PERSONA_CAPABILITIES[capability as PersonaCapability] as PersonaCapabilityDefinition).staff === true;
}

/**
 * Whether a definition not yet parsed names a staff capability, so a caller looks up the tenant's Lantern link only for
 * a definition that needs it.
 */
export function usesStaffCapability(input: unknown): boolean {
  if (typeof input !== 'object' || input === null || !('capabilities' in input)) return false;
  const { capabilities } = input;
  return Array.isArray(capabilities) && capabilities.some((capability) => typeof capability === 'string' && isStaffCapability(capability));
}

export type PersonaValidation = { ok: true; definition: PersonaDefinition } | { ok: false; errors: string[] };

function formatIssues(error: z.ZodError): string[] {
  return error.issues.map((issue) => (issue.path.length > 0 ? `${issue.path.join('.')}: ${issue.message}` : issue.message));
}

/**
 * Parses and cross-checks a definition: the slug is free, every scope type exists in the tenant, the write scope is
 * readable and goes with write_records, and a schedule goes with run_on_schedule, parses, and names a real time zone.
 */
export function validatePersonaDefinition(input: unknown, context: PersonaValidationContext): PersonaValidation {
  const parsed = PersonaDefinitionSchema.safeParse(input);
  if (!parsed.success) return { ok: false, errors: formatIssues(parsed.error) };
  const definition = parsed.data;
  const errors: string[] = [];

  const reserved = new Set<string>([...AGENT_SLUGS, ...(context.reservedSlugs ?? [])]);
  if (reserved.has(definition.slug)) errors.push(`slug "${definition.slug}" is taken by a built-in agent`);

  const capabilities = new Set(definition.capabilities);
  if (capabilities.size !== definition.capabilities.length) errors.push('capabilities: duplicate entries');

  const known = new Set(context.nodeTypes);
  for (const [field, types] of [
    ['read', definition.scope.read],
    ['write', definition.scope.write],
  ] as const) {
    const seen = new Set<string>();
    for (const type of types) {
      if (seen.has(type)) errors.push(`scope.${field}: duplicate type "${type}"`);
      seen.add(type);
      if (type !== SCOPE_ALL && !known.has(type)) errors.push(`scope.${field}: unknown object type "${type}" in this workspace`);
    }
  }
  const readsAll = definition.scope.read.includes(SCOPE_ALL);
  for (const type of definition.scope.write) {
    if (!readsAll && !definition.scope.read.includes(type)) errors.push(`scope.write: "${type}" must also be in scope.read`);
  }
  if (capabilities.has('write_records') && definition.scope.write.length === 0) errors.push('write_records needs at least one type in scope.write');
  if (!capabilities.has('write_records') && definition.scope.write.length > 0) errors.push('scope.write needs the write_records capability');
  const recordWork = definition.capabilities.some((capability) => RECORD_CAPABILITIES.includes(capability));
  if (!capabilities.has('read_records') && (recordWork || definition.scope.read.length > 0)) {
    // Every persona that works on records reads what it is scoped to; the scope is meaningless without it
    errors.push('read_records is required: the scope says what the persona may read');
  }
  if (capabilities.has('read_records') && definition.scope.read.length === 0) errors.push('read_records needs at least one type in scope.read');
  for (const capability of definition.capabilities) {
    if (isStaffCapability(capability) && context.lanternLinked !== true) errors.push(`${capability} is for Lantern staff: this workspace is not linked to a Lantern workspace`);
  }

  if (capabilities.has('review_projects') && !readsAll && !definition.scope.read.includes(PROJECTS_APP_TYPES.project)) {
    errors.push(`review_projects needs ${PROJECTS_APP_TYPES.project} in scope.read`);
  }

  if (definition.schedule && !capabilities.has('run_on_schedule')) errors.push('schedule needs the run_on_schedule capability');
  if (!definition.schedule && capabilities.has('run_on_schedule')) errors.push('run_on_schedule needs a schedule');
  if (definition.schedule) {
    const cron = parseCron(definition.schedule.cron);
    if (!cron.ok) errors.push(`schedule.cron: ${cron.error}`);
    if (definition.schedule.timeZone && !isTimeZone(definition.schedule.timeZone)) errors.push(`schedule.timeZone: "${definition.schedule.timeZone}" is not an IANA time zone`);
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true, definition };
}

/** How a persona's writes sit with plan approval, in the registry's terms. */
export interface PersonaWriteSemantics {
  /** True when any capability writes. */
  writes: boolean;
  /** True when every write capability is record-level, so its plan steps run without approval. */
  approvalFree: boolean;
  /** Capabilities that only an approved changes_data step may use. */
  adminApproved: PersonaCapability[];
}

export function personaWriteSemantics(definition: Pick<PersonaDefinition, 'capabilities'>): PersonaWriteSemantics {
  const writing = definition.capabilities.filter((capability) => (PERSONA_CAPABILITIES[capability] as PersonaCapabilityDefinition).kind === 'write');
  const adminApproved = writing.filter((capability) => (PERSONA_CAPABILITIES[capability] as PersonaCapabilityDefinition).approval === 'admin');
  return { writes: writing.length > 0, approvalFree: writing.length > 0 && adminApproved.length === 0, adminApproved };
}

/** The whole definition in words, for the approval card: an admin approves exactly this. */
export function describePersonaDefinition(definition: PersonaDefinition): string {
  const semantics = personaWriteSemantics(definition);
  const lines = [
    `Persona ${definition.displayName} (${definition.slug})`,
    `- Does: ${definition.description}`,
    `- Model: ${definition.model}, effort ${definition.effort}`,
    `- Capabilities: ${definition.capabilities.map((capability) => `${capability} (${(PERSONA_CAPABILITIES[capability] as PersonaCapabilityDefinition).label})`).join(', ')}`,
    `- Reads: ${definition.scope.read.length > 0 ? definition.scope.read.map((type) => (type === SCOPE_ALL ? 'every type' : type)).join(', ') : 'no records'}`,
    `- Writes: ${definition.scope.write.length > 0 ? definition.scope.write.map((type) => (type === SCOPE_ALL ? 'every type' : type)).join(', ') : 'nothing'}`,
    `- Approval: ${
      !semantics.writes
        ? 'it only reads; its steps run at once'
        : semantics.approvalFree
          ? 'its writes are record-level, so its steps run without approval'
          : `its record writes run without approval; ${semantics.adminApproved.join(', ')} only in a step an admin approves`
    }`,
  ];
  if (definition.schedule) lines.push(`- Schedule: ${definition.schedule.cron} (${definition.schedule.timeZone ?? 'UTC'}), brief: ${definition.schedule.brief}`);
  lines.push('- Instructions:', definition.instructions);
  return lines.join('\n');
}
